import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Timer, Star, Inbox, Medal } from "lucide-react";
import { api } from "@/lib/api";

function Tile({ icon: Icon, label, value, sub, testId, dark }) {
  return (
    <div className={`rounded-xl border-2 border-black p-3 ${dark ? "bg-black text-white" : "bg-white"}`} data-testid={testId}>
      <p className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${dark ? "text-[#FACC15]" : "text-zinc-500"}`}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className="mt-1 font-display text-2xl font-extrabold leading-none">{value}</p>
      {sub && <p className={`mt-1 text-xs ${dark ? "text-zinc-300" : "text-zinc-500"}`}>{sub}</p>}
    </div>
  );
}

const cheer = (p) => {
  if (p.rank_today === 1) return "You're today's top closer. Keep the streak alive!";
  if (p.resolved_today >= 5) return "Five-plus today — the queue fears you.";
  if (p.resolved_today > 0) return "Nice momentum. One more before the next coffee?";
  if (p.open_assigned > 0) return `You have ${p.open_assigned} ticket${p.open_assigned === 1 ? "" : "s"} waiting. Let's clear the first one.`;
  return "Fresh slate. Pick up a ticket from the queue to get going.";
};

export function MyPerformance() {
  const { data: p } = useQuery({ queryKey: ["my-performance"], queryFn: () => api.get("/me/performance").then((r) => r.data), refetchInterval: 60000 });
  if (!p) return null;
  return (
    <section className="rounded-2xl border-2 border-black bg-[#FACC15] p-4 brut-shadow fade-up" data-testid="my-performance">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.25em]">Your day · {p.day}</p>
          <p className="font-display text-lg font-bold" data-testid="my-performance-cheer">{cheer(p)}</p>
        </div>
        {p.rank_today && (
          <span className="inline-flex items-center gap-1 rounded-full border-2 border-black bg-black px-3 py-1 text-sm font-bold text-[#FACC15]" data-testid="my-rank-today">
            <Medal className="h-4 w-4" /> #{p.rank_today} today
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile icon={CheckCircle2} label="Closed today" value={p.resolved_today} sub={`team ${p.team_resolved_today} · you ${p.resolved_7d} this week`} testId="my-resolved-today" dark />
        <Tile icon={Timer} label="Avg resolution" value={p.avg_resolution_hours != null ? `${p.avg_resolution_hours}h` : "—"} sub={p.team_avg_hours ? `team ${p.team_avg_hours}h · 30 days` : "working hours, 30 days"} testId="my-avg-hours" />
        <Tile icon={Star} label={p.avg_rating != null || p.ratings_count ? "Customer rating" : "Closed this month"} value={p.avg_rating != null ? `${p.avg_rating} / 5` : p.resolved_30d} sub={p.avg_rating != null ? `${p.ratings_count} rating${p.ratings_count === 1 ? "" : "s"}` : "last 30 days"} testId="my-rating" />
        <Tile icon={Inbox} label="Open on you" value={p.open_assigned} sub={p.fastest_hours != null ? `fastest close ${p.fastest_hours}h` : "nothing assigned yet"} testId="my-open" />
      </div>
    </section>
  );
}
